import { useCallback, useEffect, useState } from 'react';
import i18n from '@/i18n';
import { notify, stateSetter } from '@/utils';
// Import DIRECT (nu prin barrel-ul '@/components') — evită ciclul hooks ↔ components.
import { IServerColumnConfig } from '@/components/table/column-config';
import {
	changeUserRole,
	deleteUser,
	fetchAppUsers,
	fetchQueryUsers,
	fetchSingleAppUser,
	fetchUsersTableConfig,
	fetchUserSanctions,
	fetchUserAffinities,
	muteAppUser,
	patchAppUserStatus,
	resetUserPassword,
	unmuteAppUser,
	updateAppUser
} from './actions';
import {
	AppUsersReturnType,
	IAppUser,
	IRestrictObj,
	IUpdateAppUser,
	IUserSanction,
	IUserAffinities
} from './types';

// Fallback stabil ca identitate pentru `data` neîncărcat — un `[]` nou la fiecare
// randare re-declanșează în lanț efectele consumatorilor cu dependență pe `data`
// („Maximum update depth exceeded").
const EMPTY_LIST: never[] = [];

export function useAppUsers<T extends IAppUser | IAppUser[]>(
	getAppUsersOnRender: boolean | undefined = undefined,
	appUserId: number | undefined = undefined,
	queryParam: string | undefined = undefined,
	withTableConfig: boolean | undefined = false
): AppUsersReturnType<T> {
	const [data, setData] = useState<T | null>(null);
	// Configurația de coloane a tabelelor de utilizatori (api/users/table-config) —
	// cerută doar când `withTableConfig` e activ; la eroare rămâne null și tabelul cade
	// silențios pe coloanele definite local.
	const [tableConfig, setTableConfig] = useState<IServerColumnConfig[] | null>(null);
	const [loadingTableConfig, setLoadingTableConfig] = useState<boolean>(
		Boolean(withTableConfig)
	);
	const [loading, setLoading] = useState<boolean>(false);
	const [loadingCTA, setLoadingCTA] = useState<boolean>(false);
	const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
	const [loadingResetPassword, setLoadingResetPassword] = useState<boolean>(false);
	const [loadingMute, setLoadingMute] = useState<boolean>(false);
	const [loadingStatus, setLoadingStatus] = useState<boolean>(false);
	const [sanctions, setSanctions] = useState<IUserSanction[]>([]);
	const [loadingSanctions, setLoadingSanctions] = useState<boolean>(false);
	const [affinities, setAffinities] = useState<IUserAffinities | null>(null);
	const [loadingAffinities, setLoadingAffinities] = useState<boolean>(false);
	const [enableQuery, setEnableQuery] = useState<boolean>(false);
	const [page, setPage] = useState<number>(0);
	const [items, setItems] = useState<number>(10);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(1);

	const handleResponse = (pageParam: number, itemsParam: number): void => {
		setTotalPages(pageParam);
		setTotalItems(itemsParam);
	};

	const getAppUsers = useCallback(
		async (pageParam: number, itemsParam: number) => {
			try {
				setLoading(true);
				const response = await fetchAppUsers(pageParam, itemsParam, handleResponse);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData]
	);

	const getQueryUsers = useCallback(
		async (query: string, queryPage: number, queryItems: number) => {
			try {
				!enableQuery && setEnableQuery(true);
				setLoading(true);
				const response = await fetchQueryUsers(
					query,
					queryPage,
					queryItems,
					handleResponse
				);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData]
	);

	const getAppUser = useCallback(
		async (userId: number | string) => {
			try {
				setLoading(true);
				const response = await fetchSingleAppUser(userId);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData]
	);

	const removeUser = useCallback(
		async (userId: number | string, onSuccess?: () => void) => {
			try {
				setLoadingCTA(true);
				const response = await deleteUser(userId);
				// BE poate răspunde cu 200 sau 204 (No Content) la ștergere.
				if (typeof response === 'number' && response >= 200 && response < 300) {
					notify.success(i18n.t('citizens.deleteSuccess'));
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingCTA(false);
			}
		},
		 
		[setData]
	);

	const editUser = useCallback(
		async (
			userId: number | string,
			updatedObject: IUpdateAppUser,
			onSuccess?: () => void
		) => {
			try {
				setLoadingUpdate(true);
				const response = await updateAppUser(userId, updatedObject);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingUpdate(false);
			}
		},
		[]
	);

	// Schimbare de rol (superadmin, users:manage-roles) — POST rol nou → DELETE rol
	// vechi (vezi changeUserRole); succesul/eșecul se notifică din pagina apelantă.
	const changeRole = useCallback(
		async (
			userId: number | string,
			newRoleId: number,
			oldRoleId?: number,
			onSuccess?: () => void
		) => {
			try {
				setLoadingUpdate(true);
				await changeUserRole(userId, newRoleId, oldRoleId);
				onSuccess && onSuccess();
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingUpdate(false);
			}
		},
		[]
	);

	const resetPassword = useCallback(
		async (userId: number | string, onSuccess?: () => void) => {
			try {
				setLoadingResetPassword(true);
				const response = await resetUserPassword(userId);
				if (response === 200) {
					notify.success(i18n.t('citizens.resetPasswordSuccess'));
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingResetPassword(false);
			}
		},
		[]
	);

	const muteUser = useCallback(
		async (userId: number | string, restrictObj: IRestrictObj, onSuccess?: () => void) => {
			try {
				setLoadingMute(true);
				const response = await muteAppUser(userId, restrictObj);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingMute(false);
			}
		},
		[]
	);

	const unmuteUser = useCallback(
		async (userId: number | string, onSuccess?: () => void) => {
			try {
				setLoadingMute(true);
				const response = await unmuteAppUser(userId);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingMute(false);
			}
		},
		[]
	);

	// PATCH api/users/:id/status — schimbă statusul (Activ / Blocat / Restricționat) prin statusId.
	const changeUserStatus = useCallback(
		async (userId: number | string, statusId: number, onSuccess?: () => void) => {
			try {
				setLoadingStatus(true);
				const response = await patchAppUserStatus(userId, statusId);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingStatus(false);
			}
		},
		[]
	);

	// GET api/users/:id/sanctions — de apelat doar când utilizatorul e restricționat / blocat (banner-e profil).
	const getUserSanctions = useCallback(async (userId: number | string) => {
		try {
			setLoadingSanctions(true);
			const response = await fetchUserSanctions(userId);
			setSanctions(response);
			return response;
		} catch {
			return [];
		} finally {
			setLoadingSanctions(false);
		}
	}, []);

	// GET api/users/:id/affinities — semnalele de afinitate pentru tab-ul „Afinități".
	const getUserAffinities = useCallback(async (userId: number | string) => {
		try {
			setLoadingAffinities(true);
			const response = await fetchUserAffinities(userId);
			setAffinities(response);
			return response;
		} catch {
			return null;
		} finally {
			setLoadingAffinities(false);
		}
	}, []);

	useEffect(() => {
		if (!withTableConfig) return undefined;
		let active = true;
		setLoadingTableConfig(true);
		fetchUsersTableConfig()
			.then((columns) => {
				if (active) setTableConfig(columns.length ? columns : null);
			})
			.catch(() => {
				// Fallback silențios: fără config BE, tabelul folosește coloanele locale.
				if (active) setTableConfig(null);
			})
			.finally(() => {
				if (active) setLoadingTableConfig(false);
			});
		return () => {
			active = false;
		};
	}, [withTableConfig]);

	useEffect(() => {
		if (getAppUsersOnRender && queryParam === undefined) {
			getAppUsers(page, items);
		}

	}, [getAppUsersOnRender, queryParam, page, items]);

	useEffect(() => {
		if (appUserId) {
			getAppUser(appUserId);
		}
		 
	}, [appUserId]);

	useEffect(() => {
		if (queryParam && appUserId === undefined) {
			getQueryUsers(queryParam, page, items);
		} else if (appUserId === undefined && enableQuery) {
			getAppUsers(page, items);
		}
		 
	}, [queryParam, appUserId, page, items]);

	return {
		data: data || (EMPTY_LIST as IAppUser[] as T),
		setData: setData as stateSetter<T>,
		loading,
		getAppUser,
		getAppUsers,
		removeUser,
		editUser,
		changeRole,
		resetPassword,
		loadingCTA,
		loadingUpdate,
		loadingResetPassword,
		loadingMute,
		loadingStatus,
		items,
		muteUser,
		changeUserStatus,
		sanctions,
		loadingSanctions,
		getUserSanctions,
		affinities,
		loadingAffinities,
		getUserAffinities,
		page,
		setItems,
		setPage,
		totalPages,
		totalItems,
		unmuteUser,
		tableConfig,
		loadingTableConfig
	};
}
